"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Twitter-style news-feed filter chips.
 *
 * URL-driven (single source of truth): the user's pinned filter
 * lives in the `topicSlug` / `tagSlug` / `marketSlug` query params.
 * Switching filters is just a navigation — the server component
 * re-fetches with the new params, so we avoid duplicating filter
 * state on the client.
 *
 * The chip strip shows:
 *   - "All" pill (clears every filter)
 *   - Active filter chip (shows the human-readable label when a
 *     filter is set; clicking removes that single filter)
 *   - Quick-jump topic chips for the common topics
 *
 * Mobile-first: the strip is horizontally scrollable at narrow
 * widths so the chip count doesn't wrap awkwardly on 375px.
 */

const QUICK_TOPICS: Array<{ slug: string; labelKey: string; fallback: string }> = [
  { slug: "politics", labelKey: "newsFeed.topics.politics", fallback: "Politics" },
  { slug: "economy", labelKey: "newsFeed.topics.economy", fallback: "Economy" },
  { slug: "sports", labelKey: "newsFeed.topics.sports", fallback: "Sports" },
  { slug: "crypto", labelKey: "newsFeed.topics.crypto", fallback: "Crypto" },
  { slug: "tech", labelKey: "newsFeed.topics.tech", fallback: "Tech" },
  { slug: "world", labelKey: "newsFeed.topics.world", fallback: "World" },
];

export function NewsFeedFilters() {
  const { t } = useTranslation();
  const sp = useSearchParams();
  const currentTopic = sp?.get("topicSlug") ?? null;
  const currentTag = sp?.get("tagSlug") ?? null;
  const currentMarket = sp?.get("marketSlug") ?? null;

  const hasFilter = useMemo(
    () => Boolean(currentTopic || currentTag || currentMarket),
    [currentTopic, currentTag, currentMarket],
  );

  // Helper that builds the href for a topic chip. Selecting a topic
  // clears tag + market so the filters don't compound surprisingly.
  const topicHref = (slug: string) => {
    const params = new URLSearchParams();
    params.set("topicSlug", slug);
    return `/feed/news?${params.toString()}`;
  };

  return (
    <nav
      aria-label={t("newsFeed.filtersLabel", "News feed filters")}
      className="mb-3 -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1"
    >
      <FilterChip
        href="/feed/news"
        active={!hasFilter}
        label={t("newsFeed.filters.all", "All")}
      />

      {/* Active free-form filter chip — surfaces tag or market when
          one is set via URL (e.g. deep-link from a market page).
          Topic filter renders below as a topic-pill so we don't
          duplicate it here. */}
      {currentTag && (
        <FilterChip
          href="/feed/news"
          active
          label={`#${currentTag}`}
          dismiss
        />
      )}
      {currentMarket && (
        <FilterChip
          href="/feed/news"
          active
          label={t("newsFeed.filters.marketPrefix", "Market: {{slug}}", {
            slug: currentMarket,
          })}
          dismiss
        />
      )}

      {QUICK_TOPICS.map((topic) => (
        <FilterChip
          key={topic.slug}
          href={topicHref(topic.slug)}
          active={currentTopic === topic.slug}
          label={t(topic.labelKey, topic.fallback)}
        />
      ))}
    </nav>
  );
}

function FilterChip({
  href,
  active,
  label,
  dismiss,
}: {
  href: string;
  active: boolean;
  label: string;
  dismiss?: boolean;
}) {
  const base =
    "inline-flex shrink-0 min-h-[32px] items-center gap-1 rounded-full border px-3 py-1 text-[12px] font-bold uppercase tracking-[0.4px] transition-colors md:text-[11px]";
  const activeStyle = active
    ? "border-[var(--color-brand)] bg-[var(--color-brand)] text-white"
    : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text2)] hover:border-[var(--color-brand)] hover:text-[var(--color-brand)]";
  return (
    <Link href={href} className={`${base} ${activeStyle}`}>
      <span>{label}</span>
      {dismiss && (
        <span aria-hidden="true" className="text-[14px] leading-none">
          ×
        </span>
      )}
    </Link>
  );
}
